import { useSetAtom } from "jotai";
import {
	CheckIcon,
	ChevronsUpDownIcon,
	FolderKanbanIcon,
	PlusIcon,
	UserPlusIcon,
} from "lucide-react";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/auth-provider";
import { ProjectIcon } from "@/components/projects/project-icon";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Shortcut } from "@/components/ui/kbd";
import { useCurrentProject } from "@/hooks/use-current-project";
import { usePermissions } from "@/hooks/use-permissions";
import { projectPath } from "@/routes/app-navigation";
import { projectDialogAtom } from "@/store/ui";
import { useRecords } from "@/sync/hooks";
import type { Project } from "@/sync/models/project";

const isActive = (project: Project) => !project.archivedAt;

export function ProjectSwitcher() {
	const { workspace, workspaces, switchWorkspace } = useAuth();
	const access = usePermissions();
	const { project: current } = useCurrentProject();
	const projects = useRecords("project", { filter: isActive });
	const openDialog = useSetAtom(projectDialogAtom);
	const navigate = useNavigate();
	const openCreate = useCallback(() => openDialog({ mode: "create" }), [openDialog]);

	return (
		<DropdownMenu>
			<DropdownMenuTrigger className="flex h-9 w-full items-center gap-2 rounded-md px-2 text-left outline-none hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-ring/30 data-[state=open]:bg-sidebar-accent">
				{current ? (
					<ProjectIcon name={current.name} />
				) : (
					<FolderKanbanIcon className="size-4 text-muted-foreground" />
				)}
				<span className="flex min-w-0 flex-1 flex-col">
					<span className="truncate text-[13px] font-medium text-foreground">
						{current?.name ?? "All projects"}
					</span>
					<span className="mono-label truncate text-[10px]">{workspace?.name}</span>
				</span>
				<ChevronsUpDownIcon className="size-3.5 text-muted-foreground" />
			</DropdownMenuTrigger>
			<DropdownMenuContent align="start" className="w-60">
				<DropdownMenuLabel>Projects</DropdownMenuLabel>
				{projects.map((project) => (
					<DropdownMenuItem
						key={project.id}
						onSelect={() => navigate(projectPath(project.id, "overview"))}
					>
						<ProjectIcon name={project.name} />
						<span className="truncate">{project.name}</span>
						{project.id === current?.id && <CheckIcon className="ml-auto size-3.5" />}
					</DropdownMenuItem>
				))}
				{projects.length === 0 && (
					<div className="px-2 py-1.5 text-[13px] text-muted-foreground">No projects yet</div>
				)}
				<DropdownMenuSeparator />
				<DropdownMenuItem onSelect={() => navigate("/projects")}>
					<FolderKanbanIcon />
					All projects
					<Shortcut keys="g o" />
				</DropdownMenuItem>
				{access.can("projects.create") && (
					<DropdownMenuItem onSelect={openCreate}>
						<PlusIcon />
						New project
						<Shortcut keys="c" />
					</DropdownMenuItem>
				)}
				{workspaces.length > 1 && (
					<>
						<DropdownMenuSeparator />
						<DropdownMenuLabel>Workspaces</DropdownMenuLabel>
						{workspaces.map((w) => (
							<DropdownMenuItem
								key={w.id}
								onSelect={() => {
									if (w.id === workspace?.id) return;
									switchWorkspace(w.id);
									navigate("/");
								}}
							>
								<span className="flex size-4 shrink-0 items-center justify-center rounded-[4px] bg-foreground font-mono text-[9px] text-background uppercase">
									{w.name.charAt(0)}
								</span>
								<span className="min-w-0 flex-1 truncate">{w.name}</span>
								<span className="text-[11px] text-muted-foreground">{w.roleName}</span>
								{w.id === workspace?.id && <CheckIcon className="size-3.5" />}
							</DropdownMenuItem>
						))}
					</>
				)}
				{access.can("members.invite") && (
					<>
						<DropdownMenuSeparator />
						<DropdownMenuItem onSelect={() => navigate("/settings/members")}>
							<UserPlusIcon />
							Invite people
						</DropdownMenuItem>
					</>
				)}
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
